/**
 * Detect the browser's local (LAN) IPv4 or mDNS hostname via WebRTC.
 *
 * Modern Chrome/Edge mDNS-obfuscate host candidates on secure origins, returning
 * `<random>.local` instead of the real 10.x/192.168.x IP. This module accepts
 * BOTH:
 *   1. Real RFC1918 / CGNAT / link-local IPv4 addresses  (best case — user has
 *      mDNS disabled, or the browser is Safari/Firefox on the yacht network)
 *   2. mDNS `<random>.local` hostnames                   (fallback — the ID is
 *      still stable per session and identifies the device on the LAN)
 *
 * We also add a public STUN server so browsers that would otherwise not emit
 * any host candidate still produce a `srflx` — useful when the STUN candidate
 * reflects the yacht router's outbound IP.
 *
 * Never throws — returns "" if it can't figure it out within 1.5 s.
 */
export async function getLocalIP() {
  return new Promise((resolve) => {
    const RTC = window.RTCPeerConnection || window.webkitRTCPeerConnection || window.mozRTCPeerConnection;
    if (!RTC) return resolve("");
    let pc;
    try {
      pc = new RTCPeerConnection({
        // STUN nudges browsers to emit at least srflx candidates on locked-down setups.
        iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
      });
    } catch { return resolve(""); }

    let best = "";
    const done = () => {
      try { pc.close(); } catch { /* */ }
      resolve(best || "");
    };
    const timeout = setTimeout(done, 1500);

    // Only accept genuine private IPv4 host candidates. mDNS `xxx.local`
    // hostnames are randomised per WebRTC session on modern Chrome/Safari/
    // Firefox — they change every page load, so they are useless as a device
    // identifier. If we can't get a numeric LAN IP we return "" and the UI
    // shows "LAN hidden" (with a tooltip about the browser flag) — the stable
    // `device_id` cookie is the reliable per-device fingerprint anyway.
    const isPrivateIPv4 = (s) =>
      /^(10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.|169\.254\.|100\.(6[4-9]|[7-9]\d|1[01]\d|12[0-7])\.)/.test(s);

    try { pc.createDataChannel(""); } catch { clearTimeout(timeout); return done(); }

    pc.onicecandidate = (e) => {
      const cand = e.candidate?.candidate || "";
      if (!cand) return;
      if (!/\btyp\s+host\b/.test(cand)) return;   // never srflx (public reflex)
      for (const t of cand.split(/\s+/)) {
        if (isPrivateIPv4(t)) { best = t; break; }
      }
      if (best) { clearTimeout(timeout); done(); }
    };

    pc.createOffer().then((o) => pc.setLocalDescription(o)).catch(done);
  });
}

// Cache within the page lifecycle — WebRTC probe once per session.
let _cached = null;
export async function cachedLocalIP() {
  if (_cached !== null) return _cached;
  _cached = await getLocalIP();
  return _cached;
}
